import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Square } from 'lucide-react';
import api from '../services/api';
import { useAmoebaConfig } from '../hooks/useAmoebaConfig';
import { TIMER_EVENT, formatElapsed } from '../utils/timeFormat';

// Running-timer indicator for the navbar. Polls the server (so a timer started
// on another device or tab shows up) and refreshes at once when the Tracker
// page starts or stops a timer.
const TimerChip = () => {
    const { hasAmoeba } = useAmoebaConfig();
    const enabled = hasAmoeba('TIME') && localStorage.getItem('isAuthenticated') !== null;
    const [entry, setEntry] = useState(null);
    const [now, setNow] = useState(() => Date.now());

    // `background`: the 30 s poll is marked so usage analytics counts it per
    // day instead of treating it as the person actively using the app.
    const refresh = useCallback(async (background = false) => {
        try {
            const res = await api.get('/time/timer/current', background === true ? { headers: { 'X-Usage-Background': '1' } } : undefined);
            setEntry(res.data);
        } catch {
            setEntry(null);
        }
    }, []);

    useEffect(() => {
        if (!enabled) return undefined;
        refresh();
        const poll = setInterval(() => refresh(true), 30000);
        const onChange = () => refresh();
        window.addEventListener(TIMER_EVENT, onChange);
        return () => { clearInterval(poll); window.removeEventListener(TIMER_EVENT, onChange); };
    }, [enabled, refresh]);

    useEffect(() => {
        if (!entry) return undefined;
        const tick = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(tick);
    }, [entry]);

    if (!enabled || !entry) return null;

    const stop = async () => {
        try { await api.post('/time/timer/stop'); } catch { /* the next refresh shows the truth */ }
        refresh();
        window.dispatchEvent(new Event(TIMER_EVENT));
    };

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.35rem 0.75rem', borderRadius: 999, background: 'var(--accent-subtle)', fontSize: '0.85rem' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444' }} />
            <Link to="/time" className="navbar-hide-label" style={{ textDecoration: 'none', color: 'var(--text-secondary)' }} title={entry.description || entry.project?.name}>
                {entry.project?.name} · <strong>{formatElapsed(now - new Date(entry.startedAt).getTime())}</strong>
            </Link>
            <button onClick={stop} aria-label="Stop timer" title="Stop timer" style={{ border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', color: '#ef4444' }}>
                <Square size={14} fill="currentColor" />
            </button>
        </div>
    );
};

export default TimerChip;
